'use client'
import React from 'react'
import classNames from 'classnames'
import FlightIcon from '../../flight-icon'
import {
	ComboBox,
	type TComboBoxItem,
	type ComboBoxSearchInputProps,
	ComboBoxListProps,
} from '@hashicorp/react-mds/src/hds/components/combo-box-primitive'

type SuperSelectRootProps = {
	children: React.ReactNode
	onChangeSelectedOption?: (option: TComboBoxItem) => void
	onChangeVFocusedOption?: (option: TComboBoxItem) => void
}

function SuperSelectRoot({
	children,
	onChangeSelectedOption,
	onChangeVFocusedOption,
}: SuperSelectRootProps) {
	return (
		<ComboBox.Root
			onChangeSelectedOption={onChangeSelectedOption}
			onChangeVFocusedOption={onChangeVFocusedOption}
		>
			<div className="hds-form-super-select__root">{children}</div>
		</ComboBox.Root>
	)
}

interface SuperSelectTriggerProps {
	className?: string
}

function SuperSelectTrigger({ className }: SuperSelectTriggerProps) {
	return (
		<ComboBox.Trigger
			className={classNames('hds-form-super-select__trigger', className)}
		>
			<ComboBox.Value />
		</ComboBox.Trigger>
	)
}

type SuperSelectPopoverProps = {
	children: React.ReactNode
}

function SuperSelectPopover({ children }: SuperSelectPopoverProps) {
	return (
		<ComboBox.Popover className="hds-form-super-select__popover">
			{children}
		</ComboBox.Popover>
	)
}

export type SuperSelectSearchInputProps = ComboBoxSearchInputProps

function SuperSelectSearchInput({
	placeholder = 'Search',
	value,
	onChange,
	onBlur,
}: SuperSelectSearchInputProps) {
	return (
		<div className="hds-form-super-select__search-input-wrapper">
			<ComboBox.SearchInput
				placeholder={placeholder}
				value={value}
				onChange={onChange}
				onBlur={onBlur}
			/>
		</div>
	)
}

type SuperSelectListProps = ComboBoxListProps

function SuperSelectList({
	label,
	children,
	className,
	maxHeight,
	onScroll,
}: SuperSelectListProps) {
	return (
		<ComboBox.List
			label={label}
			className={classNames('hds-form-super-select__list', className)}
			maxHeight={maxHeight}
			onScroll={onScroll}
		>
			{children}
		</ComboBox.List>
	)
}

export interface SuperSelectOptionProps extends TComboBoxItem {
	isLoading?: boolean
	/**
	 * Leading icon. Acceptable value: any [icon](https://helios.hashicorp.design/icons/library) name.
	 */
	icon?: string
	/**
	 * Trailing icon. Acceptable value: any [icon](https://helios.hashicorp.design/icons/library) name.
	 */
	trailingIcon?: string
	className?: string
}

function SuperSelectOption({
	value,
	label,
	isLoading,
	icon,
	className,
	trailingIcon,
}: SuperSelectOptionProps) {
	return (
		<ComboBox.Item
			value={value}
			label={label}
			className={classNames('hds-form-super-select__option', className)}
		>
			{isLoading ? (
				<div className="hds-dropdown-list-item__interactive-loading-wrapper">
					<div className="hds-dropdown-list-item__interactive-icon hds-dropdown-list-item__interactive-icon--leading">
						<FlightIcon name="loading" isInlineBlock={false} />
					</div>
					<span className="hds-form-super-select__option-text hds-font-weight-regular hds-typography-body-200">
						{label}
					</span>
				</div>
			) : (
				<>
					{icon && (
						<span className="hds-dropdown-list-item__interactive-icon hds-dropdown-list-item__interactive-icon--leading">
							<FlightIcon name={icon} isInlineBlock={false} />
						</span>
					)}
					<span className="hds-form-super-select__option-text hds-font-weight-regular hds-typography-body-200">
						{label}
					</span>
					{trailingIcon && (
						<span className="hds-dropdown-list-item__interactive-icon hds-dropdown-list-item__interactive-icon--trailing">
							<FlightIcon name={trailingIcon} isInlineBlock={false} />
						</span>
					)}
				</>
			)}
		</ComboBox.Item>
	)
}

export const SuperSelect = Object.freeze({
	Root: SuperSelectRoot,
	Trigger: SuperSelectTrigger,
	Popover: SuperSelectPopover,
	/**
	 * Note that this only acts as a view layer,
	 * it does not participate in the search logic.
	 * This is left for the consumer to implement.
	 */
	SearchInput: SuperSelectSearchInput,
	List: SuperSelectList,
	Option: SuperSelectOption,
})

export type { TComboBoxItem as SuperSelectOption }
